// src/views/source-connections/components/AddConnectionWizard.jsx

import React, { useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon as fa_icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { toast } from "../../../components/ui-components/Toast";
import FormButton from "../../../components/form-components/FormButton";
import StepSelectSource from "./wizard/StepSelectSource";
import StepConfigure from "./wizard/StepConfigure";
import StepReview from "./wizard/StepReview";
import { createSourceConnection } from "../api";

const STEPS = [
  { key: "select", label: "Select Source" },
  { key: "configure", label: "Configure" },
  { key: "review", label: "Review" },
];

const StepIndicator = ({ currentStep }) => (
  <div className="flex items-center px-7 py-3.5 border-b border-border bg-surface-2">
    {STEPS.map((step, i) => (
      <React.Fragment key={step.key}>
        <div className="flex items-center gap-2.5">
          <div
            className={`w-7 h-7 min-w-[28px] min-h-[28px] rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 aspect-square ${
              i < currentStep
                ? "bg-success text-white"
                : i === currentStep
                ? "bg-primary text-white"
                : "bg-card text-text-2 border border-border"
            }`}
          >
            {i < currentStep ? "✓" : i + 1}
          </div>
          <span className={`text-[13px] ${i <= currentStep ? "font-medium text-text" : "text-text-2"}`}>
            {step.label}
          </span>
        </div>
        {i < STEPS.length - 1 && (
          <div className={`flex-1 h-px mx-5 ${i < currentStep ? "bg-success" : "bg-border"}`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

const AddConnectionWizard = ({ show, close }) => {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({});

  const resetWizard = useCallback(() => {
    setStep(0);
    setSelectedType(null);
    setFormData({});
  }, []);

  const handleClose = useCallback(() => {
    resetWizard();
    close();
  }, [close, resetWizard]);

  const saveMutation = useMutation({
    mutationFn: (payload) => createSourceConnection(payload),
    onSuccess: () => {
      toast.success("Connection created successfully");
      queryClient.invalidateQueries({ queryKey: ["source-connections"] });
      handleClose();
    },
    onError: (err) => toast.error(err?.message || "Failed to create connection"),
  });

  const handleSave = () => {
    const payload = {
      source_type: selectedType.id,
      connection_name: formData.connection_name,
      auth_method: selectedType.authMethod,
      data_types: formData.dataTypes || selectedType.dataTypes.filter((d) => d.default).map((d) => d.id),
      metadata: {},
    };

    const credentialFields = new Set(["api_key", "oauth_token", "password", "token", "secret"]);
    selectedType.fields.forEach((f) => {
      if (formData[f.name]) {
        if (credentialFields.has(f.name) || f.type === "password") {
          payload.credential = formData[f.name];
        } else {
          payload.metadata[f.name] = formData[f.name];
        }
      }
    });

    if (selectedType.authMethod === "service_account_key" && formData.fileContent) {
      payload.credential = formData.fileContent;
    }

    if (selectedType.authMethod === "oauth_token" && formData.oauth_token) {
      payload.credential = formData.oauth_token;
    }

    if (selectedType.authMethod === "csv_upload" && formData.file) {
      payload.file = formData.file;
    }

    if (formData.associated_provider) {
      payload.metadata.associated_provider = formData.associated_provider;
    }

    saveMutation.mutate(payload);
  };

  const canProceed = () => {
    if (step === 0) return !!selectedType;
    if (step === 1) {
      if (!formData.connection_name?.trim()) return false;
      const requiredFields = selectedType.fields.filter((f) => f.required);
      for (const f of requiredFields) {
        const val = formData[f.name];
        if (val === undefined || val === null || val === "") return false;
        if (typeof val === "string" && !val.trim()) return false;
      }
      if (selectedType.authMethod === "service_account_key" && !formData.fileContent) return false;
      if (selectedType.authMethod === "oauth_token" && !formData.oauth_token) return false;
      if (selectedType.authMethod === "csv_upload" && !formData.file) return false;
      return true;
    }
    return true;
  };

  if (!show) return null;

  return ReactDOM.createPortal(
    <div
      className="z-[100] fixed inset-0 flex items-center justify-center cursor-default"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)" }}
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-label="Add Source Connection"
    >
      <div
        className="w-[960px] max-h-[90vh] bg-card relative border border-card-bd rounded-card flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-start px-7 pt-6 pb-4 border-b border-border">
          <div>
            <div className="text-lg font-semibold text-text">Add Source Connection</div>
            <div className="text-[13px] text-text-2 mt-1">Select a data source to configure credentials and begin importing.</div>
          </div>
          <button
            onClick={handleClose}
            className="text-text-2 hover:text-text p-1 -mt-1"
            aria-label="Close dialog"
          >
            <FontAwesomeIcon icon={fa_icon({ name: "xmark", style: "light" })} className="w-5 h-5" />
          </button>
        </div>

        {/* Step Indicator */}
        <StepIndicator currentStep={step} />

        {/* Step Content */}
        <div className="flex-1 overflow-hidden">
          {step === 0 && (
            <StepSelectSource
              selectedType={selectedType}
              onSelect={setSelectedType}
            />
          )}
          {step === 1 && selectedType && (
            <StepConfigure
              sourceType={selectedType}
              formData={formData}
              onChange={setFormData}
            />
          )}
          {step === 2 && selectedType && (
            <StepReview
              sourceType={selectedType}
              formData={formData}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-7 py-4 border-t border-border bg-surface-2">
          <FormButton
            title={step === 0 ? "Cancel" : "← Back"}
            onClick={step === 0 ? handleClose : () => setStep(step - 1)}
          />
          {step < 2 ? (
            <FormButton
              title="Continue →"
              style="primary"
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
            />
          ) : (
            <FormButton
              title="Save Connection"
              style="primary"
              onClick={handleSave}
              disabled={saveMutation.isPending}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddConnectionWizard;
